(() => {
  const STORAGE_KEY = 'wardrobeWishlistV1';
  const COOKIE_KEY = 'wardrobe_wishlist';
  const selectors = {
    button: '[data-wishlist-button]',
    count: '[data-wishlist-count]',
    page: '[data-wishlist-page]',
    grid: '[data-wishlist-grid]',
    empty: '[data-wishlist-empty]',
  };

  const readCookie = () => {
    const cookie = document.cookie
      .split('; ')
      .find((entry) => entry.startsWith(`${COOKIE_KEY}=`));

    if (!cookie) return '[]';

    try {
      return decodeURIComponent(cookie.slice(COOKIE_KEY.length + 1));
    } catch (_error) {
      return '[]';
    }
  };

  const getSavedValue = () => {
    try {
      if (window.localStorage) {
        return window.localStorage.getItem(STORAGE_KEY) || readCookie();
      }
    } catch (_error) {
      // Some privacy modes and embedded previews block localStorage.
    }

    return readCookie();
  };

  const read = () => {
    try {
      const saved = JSON.parse(getSavedValue() || '[]');
      return Array.isArray(saved) ? saved.filter((item) => item && item.handle) : [];
    } catch (_error) {
      return [];
    }
  };

  const write = (items) => {
    const value = JSON.stringify(items);
    let savedToLocalStorage = false;

    try {
      if (window.localStorage) {
        window.localStorage.setItem(STORAGE_KEY, value);
        savedToLocalStorage = true;
      }
    } catch (_error) {
      // Fall through to the cookie fallback.
    }

    if (!savedToLocalStorage) {
      document.cookie = `${COOKIE_KEY}=${encodeURIComponent(value)}; path=/; max-age=31536000; SameSite=Lax`;
    }

    document.dispatchEvent(new CustomEvent('wardrobe:wishlist:updated', { detail: { items } }));
  };

  const fromButton = (button) => ({
    handle: button.dataset.productHandle || '',
    url: button.dataset.productUrl || '',
    title: button.dataset.productTitle || '',
    image: button.dataset.productImage || '',
    price: button.dataset.productPrice || '',
    comparePrice: button.dataset.productComparePrice || '',
    available: button.dataset.productAvailable !== 'false',
    addedAt: Date.now(),
  });

  const updateCount = (items = read()) => {
    document.querySelectorAll(selectors.count).forEach((count) => {
      const wrappedCount =
        count.closest('.wardrobe-mobile-drawer__quick-links') ||
        count.closest('.wd-mobile-bottom-nav__item');

      count.textContent = wrappedCount ? `(${items.length})` : String(items.length);
      count.hidden = items.length === 0;
    });
  };

  const syncButtons = (items = read()) => {
    const handles = new Set(items.map((item) => item.handle));

    document.querySelectorAll(selectors.button).forEach((button) => {
      const active = handles.has(button.dataset.productHandle);
      button.classList.toggle('is-wishlisted', active);
      button.setAttribute('aria-pressed', String(active));

      const title = button.dataset.productTitle || 'product';
      button.setAttribute(
        'aria-label',
        `${active ? 'Remove' : 'Add'} ${title} ${active ? 'from' : 'to'} wishlist`
      );
    });
  };

  const showToast = (message) => {
    let toast = document.querySelector('[data-wishlist-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'wardrobe-wishlist-toast';
      toast.dataset.wishlistToast = '';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }

    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(showToast.timeout);
    showToast.timeout = window.setTimeout(() => toast.classList.remove('is-visible'), 2200);
  };

  const toggle = (button) => {
    const product = fromButton(button);
    if (!product.handle) return { items: read(), added: false };

    const items = read();
    const existingIndex = items.findIndex((item) => item.handle === product.handle);
    let added = true;

    if (existingIndex >= 0) {
      items.splice(existingIndex, 1);
      added = false;
    } else {
      items.unshift(product);
    }

    write(items);
    return { items, added };
  };

  const moveFromCart = async (button) => {
    const product = fromButton(button);
    const items = read();

    if (!items.some((item) => item.handle === product.handle)) {
      items.unshift(product);
      write(items);
    }

    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: button.dataset.cartLineKey, quantity: 0 }),
      });

      if (!response.ok) throw new Error('Unable to update cart');
      window.location.reload();
    } catch (_error) {
      button.removeAttribute('aria-busy');
      syncButtons(items);
      updateCount(items);
      showToast('Saved to wishlist. Please remove it from the cart manually.');
    }
  };

  const createElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  const renderPage = () => {
    const page = document.querySelector(selectors.page);
    if (!page) return;

    const grid = page.querySelector(selectors.grid);
    const empty = page.querySelector(selectors.empty);
    const items = read();
    grid.replaceChildren();
    empty.hidden = items.length > 0;

    items.forEach((item) => {
      const card = createElement('article', 'wardrobe-wishlist-card');
      const media = createElement('a', 'wardrobe-wishlist-card__media');
      media.href = item.url || `/products/${encodeURIComponent(item.handle)}`;
      media.setAttribute('aria-label', item.title);

      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = item.title;
        image.loading = 'lazy';
        image.width = 700;
        image.height = 840;
        media.appendChild(image);
      } else {
        media.appendChild(createElement('span', 'wardrobe-wishlist-card__placeholder', 'The Wardrobe'));
      }

      const remove = createElement('button', 'wardrobe-wishlist-card__remove');
      remove.type = 'button';
      remove.dataset.wishlistRemove = item.handle;
      remove.setAttribute('aria-label', `Remove ${item.title} from wishlist`);
      remove.innerHTML =
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20S4.5 15.7 4.5 9.5C4.5 7.1 6.3 5.5 8.5 5.5c1.5 0 2.7.8 3.5 2 .8-1.2 2-2 3.5-2 2.2 0 4 1.6 4 4 0 6.2-7.5 10.5-7.5 10.5Z"/></svg>';
      media.appendChild(remove);

      const info = createElement('div', 'wardrobe-wishlist-card__info');
      const title = createElement('h2', 'wardrobe-wishlist-card__title');
      const titleLink = createElement('a', '', item.title);
      titleLink.href = media.href;
      title.appendChild(titleLink);

      const prices = createElement('div', 'wardrobe-wishlist-card__prices');
      prices.appendChild(createElement('span', 'wardrobe-wishlist-card__price', item.price));
      if (item.comparePrice) {
        prices.appendChild(createElement('span', 'wardrobe-wishlist-card__compare', item.comparePrice));
      }

      const actions = createElement('div', 'wardrobe-wishlist-card__actions');
      const view = createElement('a', 'wardrobe-wishlist-card__view', 'View product');
      view.href = media.href;
      actions.appendChild(view);

      info.append(title, prices, actions);
      card.append(media, info);
      grid.appendChild(card);
    });
  };

  document.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-wishlist-remove]');
    if (remove) {
      event.preventDefault();
      event.stopPropagation();
      const items = read().filter((item) => item.handle !== remove.dataset.wishlistRemove);
      write(items);
      renderPage();
      showToast('Removed from wishlist');
      return;
    }

    const button = event.target.closest(selectors.button);
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();

    if (button.hasAttribute('data-wishlist-move-from-cart')) {
      moveFromCart(button);
      return;
    }

    const result = toggle(button);
    syncButtons(result.items);
    updateCount(result.items);
    renderPage();
    showToast(result.added ? 'Added to wishlist' : 'Removed from wishlist');
  });

  document.addEventListener('wardrobe:wishlist:updated', (event) => {
    const items = event.detail?.items || read();
    syncButtons(items);
    updateCount(items);
  });

  const initialize = () => {
    const items = read();
    syncButtons(items);
    updateCount(items);
    renderPage();

    const observer = new MutationObserver(() => syncButtons(read()));
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
